import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { detectSubscriptions, totalAnnualSubscriptionCost, DetectedSubscription } from '@/lib/subscription-detector';
import { formatAUD } from '@/lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RefreshCw, DollarSign, AlertTriangle, CheckCircle2, Eye, Trash2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

const FREQ_LABEL: Record<string, string> = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
};

export default function SubscriptionsPage() {
  const { data, updateData } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const subs = useMemo(() => detectSubscriptions(data.transactions), [data.transactions]);
  const annualTotal = useMemo(() => totalAnnualSubscriptionCost(subs), [subs]);

  const [selected, setSelected] = useState<DetectedSubscription | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<DetectedSubscription | null>(null);

  const handleCancel = async (sub: DetectedSubscription) => {
    // Remove all transactions matching this subscription description
    const key = sub.description.trim().toLowerCase();
    await updateData(prev => ({
      ...prev,
      transactions: prev.transactions.filter(
        t => !(t.type === 'expense' && t.description.replace(/^\[basiq:[^\]]+\]\s*/, '').trim().toLowerCase() === key)
      ),
    }));
    setConfirmCancel(null);
    toast({
      title: 'Subscription removed',
      description: `All ${sub.occurrences} transactions for "${sub.description}" have been removed.`,
    });
  };

  return (
    <div className="px-4 pt-6 pb-24">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <h1 className="mb-1 font-display text-2xl font-bold">Subscriptions</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Recurring charges detected from your transaction history.
      </p>

      {/* Summary cards */}
      <div className="mb-6 grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Detected</p>
            <p className="text-2xl font-bold">{subs.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Monthly</p>
            <p className="text-lg font-bold">{formatAUD(annualTotal / 12)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Annual</p>
            <p className="text-lg font-bold text-destructive">{formatAUD(annualTotal)}</p>
          </CardContent>
        </Card>
      </div>

      {subs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-8 text-center">
            <CheckCircle2 className="h-10 w-10 text-primary" />
            <p className="font-medium">No recurring charges detected</p>
            <p className="text-sm text-muted-foreground">
              Add more transactions to help detect subscriptions automatically.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <RefreshCw className="h-4 w-4 text-primary" />
              All Detected Subscriptions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="hidden sm:table-cell">Freq</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subs.map((sub, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <p className="font-medium text-sm truncate max-w-[140px]">{sub.description}</p>
                      <p className="text-[10px] text-muted-foreground sm:hidden">
                        {FREQ_LABEL[sub.frequency]} · {sub.occurrences} charges
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <p className="text-sm font-semibold">{formatAUD(sub.amount)}</p>
                      <p className="text-[10px] text-muted-foreground">{formatAUD(sub.estimatedAnnualCost)}/yr</p>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="secondary" className="text-[10px]">
                        {FREQ_LABEL[sub.frequency]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setSelected(sub)}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setConfirmCancel(sub)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Audit detail dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selected?.description}</DialogTitle>
            <DialogDescription>Subscription audit details</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-semibold">{formatAUD(selected.amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Frequency</span><Badge variant="secondary">{FREQ_LABEL[selected.frequency]}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total charges</span><span>{selected.occurrences}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Last charge</span><span>{new Date(selected.lastCharge).toLocaleDateString('en-AU')}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Est. annual cost</span><span className="font-semibold text-destructive">{formatAUD(selected.estimatedAnnualCost)}</span></div>
              <div className="rounded-lg border border-dashed p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <p className="text-xs text-muted-foreground">
                    Review whether you still need this subscription. Even small recurring charges add up — this one costs you <strong>{formatAUD(selected.estimatedAnnualCost)}</strong> per year.
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
            <Button variant="destructive" onClick={() => { setConfirmCancel(selected); setSelected(null); }}>
              Remove Transactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <Dialog open={!!confirmCancel} onOpenChange={() => setConfirmCancel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove subscription transactions?</DialogTitle>
            <DialogDescription>
              This will delete all {confirmCancel?.occurrences} expense transactions matching "{confirmCancel?.description}". This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCancel(null)}>Keep</Button>
            <Button variant="destructive" onClick={() => confirmCancel && handleCancel(confirmCancel)}>
              Delete All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
